// frontend/src/facturas/AdminContabilidad.js
// ═══════════════════════════════════════════════════════════════════════════
// CENTRO DE CONTABILIDAD - UrbanQ
// Diseño basado en AdminSupport.js
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { API_URL, API_ENDPOINTS } from '../config/api';
import PaymentConfigModal from '../admin/PaymentConfigModal';
import './AdminContabilidad.css';
import '../styles/AdminTabs.css';
import '../finanzas/AdminFinanzas.css';
import {
    FiDollarSign, FiFileText, FiTrendingUp, FiSettings,
    FiClock, FiCheck, FiAlertCircle, FiX,
    FiRefreshCw, FiDownload, FiEye, FiPrinter,
    FiUsers, FiCreditCard, FiBarChart2, FiPieChart,
    FiCalendar, FiFilter, FiSearch, FiChevronRight,
    FiCheckCircle, FiAlertTriangle, FiInfo, FiExternalLink,
    FiTarget
} from 'react-icons/fi';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { exportarContabilidadExcel } from '../utils/excelExport';

// Importar componentes financieros
import FinancialDashboardTab from '../finanzas/FinancialDashboardTab';
import IncomeTab from '../finanzas/IncomeTab';
import ExpenseTab from '../finanzas/ExpenseTab';
import BudgetTab from '../finanzas/BudgetTab';
import AnnualSummaryTab from '../finanzas/AnnualSummaryTab';
import FinancialConfigTab from '../finanzas/FinancialConfigTab';
import FinancialCalendar from '../finanzas/FinancialCalendar';
import FinancialSubmenu, { menuOptions } from '../finanzas/FinancialSubmenu';
import YearMonthSelector from '../finanzas/YearMonthSelector';
import '../finanzas/FinancialSubmenu.css';
import '../finanzas/FinancialCalendar.css';
import '../finanzas/YearMonthSelector.css';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

export default function AdminContabilidad() {
    // Estados
    const [activeTab, setActiveTab] = useState('resumen');
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    // Estados de datos
    const [payments, setPayments] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [paymentConfig, setPaymentConfig] = useState({
        paypalme: null,
        qrCode: null,
        bankAccount: null
    });

    // Estados de métricas
    const [metrics, setMetrics] = useState({
        ingresosMes: 0,
        pagosCompletados: 0,
        facturasGeneradas: 0,
        pendienteCobrar: 0
    });

    // Estados de filtros
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth());
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());

    // Estados para tabs financieros
    const [finYear, setFinYear] = useState(new Date().getFullYear());
    const [finMonth, setFinMonth] = useState(new Date().getMonth() + 1);
    const [showFinancialMenu, setShowFinancialMenu] = useState(false);
    const [financialSubTab, setFinancialSubTab] = useState(null);

    // Estado del modal de configuración
    const [showPaymentConfigModal, setShowPaymentConfigModal] = useState(false);

    // Obtener usuario actual
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES AUXILIARES
    // ═══════════════════════════════════════════════════════════════════════

    const formatCurrency = (amount) => {
        if (!amount) return '$0.00';
        return `$${parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-EC', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return 'Fecha inválida';
        }
    };

    const getMonthName = (monthIndex) => {
        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                       'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        return meses[monthIndex] || '';
    };

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES DE API
    // ═══════════════════════════════════════════════════════════════════════

    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(API_ENDPOINTS.payments, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.payments) {
                const formattedPayments = data.payments.map(payment => ({
                    id: payment.id,
                    concepto: payment.concept,
                    monto: parseFloat(payment.amount || 0),
                    fecha: payment.due_date || payment.created_at,
                    fechaFormateada: formatDate(payment.due_date || payment.created_at),
                    descripcion: payment.description || '',
                    apartamento: payment.apartment || 'N/A',
                    usuario: payment.user_name || payment.username || 'Usuario',
                    usuarioId: payment.user_id,
                    estado: payment.status === 'Paid' ? 'pagado' :
                            payment.status === 'PendingApproval' ? 'pendiente_validacion' : 'pendiente',
                    comprobanteUrl: payment.receipt_path || null
                }));

                setPayments(formattedPayments);
                calculateMetrics(formattedPayments);
                generateInvoices(formattedPayments);
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
            showNotification('Error al cargar los pagos', 'error');
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchPaymentConfig = useCallback(async () => {
        try {
            const headers = { 'Authorization': `Bearer ${token}` };
            let paypalConfigured = false;
            let qrConfigured = false;
            let bankConfigured = false;
            let paypalUsername = null;

            // 1. Obtener estado de PayPal.me desde /condominiums/paypal-config
            try {
                const paypalResponse = await fetch(API_ENDPOINTS.condominiumPayPalConfig, { headers });
                if (paypalResponse.ok) {
                    const paypalData = await paypalResponse.json();
                    // Verificar data.data.paypalMeUsername
                    if (paypalData.success && paypalData.data && paypalData.data.paypalMeUsername) {
                        paypalConfigured = true;
                        paypalUsername = paypalData.data.paypalMeUsername;
                    }
                }
            } catch (e) {
                console.error('Error fetching PayPal config:', e);
            }

            // 2. Obtener estado de QR y Banco desde /payment-config/check
            try {
                const configResponse = await fetch(API_ENDPOINTS.paymentConfigCheck, { headers });
                if (configResponse.ok) {
                    const configData = await configResponse.json();
                    // Verificar qr_configured y bank_configured
                    qrConfigured = configData.qr_configured || false;
                    bankConfigured = configData.bank_configured || false;
                }
            } catch (e) {
                console.error('Error fetching payment config check:', e);
            }

            setPaymentConfig({
                paypalme: paypalConfigured ? paypalUsername : null,
                qrCode: qrConfigured,
                bankAccount: bankConfigured
            });
        } catch (error) {
            console.error('Error fetching payment config:', error);
        }
    }, [token]);

    const calculateMetrics = (paymentsData) => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const pagadosEsteMes = paymentsData.filter(p => {
            const fecha = new Date(p.fecha);
            return p.estado === 'pagado' &&
                   fecha.getMonth() === currentMonth &&
                   fecha.getFullYear() === currentYear;
        });

        const pagadosTodos = paymentsData.filter(p => p.estado === 'pagado');
        const pendientes = paymentsData.filter(p => p.estado === 'pendiente' || p.estado === 'pendiente_validacion');

        setMetrics({
            ingresosMes: pagadosEsteMes.reduce((sum, p) => sum + p.monto, 0),
            pagosCompletados: pagadosTodos.length,
            facturasGeneradas: pagadosTodos.length,
            pendienteCobrar: pendientes.reduce((sum, p) => sum + p.monto, 0)
        });
    };

    const generateInvoices = (paymentsData) => {
        const pagados = paymentsData.filter(p => p.estado === 'pagado');
        const facturasGeneradas = pagados.map((pago, index) => ({
            id: pago.id,
            numero: `RDP-${new Date().getFullYear()}-${String(pago.id).padStart(3, '0')}`,
            usuario: pago.usuario,
            apartamento: pago.apartamento,
            concepto: pago.concepto,
            monto: pago.monto,
            fecha: pago.fechaFormateada,
            fechaOriginal: pago.fecha,
            estado: 'emitida'
        }));
        setInvoices(facturasGeneradas);
    };

    // Cargar datos iniciales
    useEffect(() => {
        fetchPayments();
        fetchPaymentConfig();
    }, [fetchPayments, fetchPaymentConfig]);

    // ═══════════════════════════════════════════════════════════════════════
    // NOTIFICACIONES
    // ═══════════════════════════════════════════════════════════════════════

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES DE ACCIONES
    // ═══════════════════════════════════════════════════════════════════════

    // Helper para extraer mes del concepto
    const getMesFromConcepto = (concepto) => {
        if (!concepto) return '';
        const meses = {
            'enero': 'Ene', 'febrero': 'Feb', 'marzo': 'Mar', 'abril': 'Abr',
            'mayo': 'May', 'junio': 'Jun', 'julio': 'Jul', 'agosto': 'Ago',
            'septiembre': 'Sep', 'octubre': 'Oct', 'noviembre': 'Nov', 'diciembre': 'Dic'
        };
        const lower = concepto.toLowerCase();
        for (const [mes, abrev] of Object.entries(meses)) {
            if (lower.includes(mes)) return abrev;
        }
        return '';
    };

    // Helper para categorizar concepto
    const categorizeConcepto = (concepto) => {
        if (!concepto) return 'alicuota';
        const lower = concepto.toLowerCase();
        if (lower.includes('extraordinari')) return 'extraordinaria';
        if (lower.includes('parqueadero') || lower.includes('parking')) return 'parqueadero';
        if (lower.includes('agua')) return 'agua';
        if (lower.includes('alicuota') || lower.includes('expensa') || lower.includes('cuota')) return 'alicuota';
        return 'otros';
    };

    const viewInvoice = (factura) => {
        const mesAbrev = getMesFromConcepto(factura.concepto);
        const categoria = categorizeConcepto(factura.concepto);
        const monto = formatCurrency(factura.monto);

        const conceptRows = [
            { key: 'alicuota', desc: 'Alicuota Ordinaria' },
            { key: 'extraordinaria', desc: 'Alicuota Extraordinaria' },
            { key: 'parqueadero', desc: 'Parqueadero' },
            { key: 'agua', desc: 'Agua' },
            { key: 'otros', desc: 'Otros' },
        ];

        const tableRows = conceptRows.map(row => {
            const isActive = row.key === categoria;
            const tdStyle = isActive
                ? 'padding:10px 12px;border:1px solid #e2e8f0;color:#1e293b;'
                : 'padding:10px 12px;border:1px solid #e2e8f0;color:#94a3b8;';
            return `<tr>
                <td style="${tdStyle}">${isActive ? `<strong>${factura.concepto}</strong>` : row.desc}</td>
                <td style="${tdStyle}text-align:center;">${isActive ? mesAbrev : ''}</td>
                <td style="${tdStyle}text-align:right;">${isActive ? monto : ''}</td>
                <td style="${tdStyle}text-align:right;">${isActive ? `<strong>${monto}</strong>` : ''}</td>
            </tr>`;
        }).join('');

        const reciboHTML = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Recibo ${factura.numero} - UrbanQ</title>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Poppins', 'Segoe UI', Arial, sans-serif; margin: 0; background: #f8f9fa; padding: 20px; }
                .recibo-container { background: white; max-width: 780px; margin: 0 auto; padding: 30px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); position: relative; overflow: hidden; }
                .watermark { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%) rotate(-45deg); font-size: 72px; color: rgba(67,56,202,0.04); font-weight: bold; z-index: 0; pointer-events: none; letter-spacing: 8px; }
                .actions-bar { background: #4338ca; padding: 14px 20px; text-align: center; max-width: 780px; margin: 0 auto 0; border-radius: 8px 8px 0 0; }
                .actions-bar button { background: white; color: #4338ca; border: none; padding: 10px 20px; margin: 0 5px; border-radius: 5px; cursor: pointer; font-weight: 600; font-size: 14px; }
                .actions-bar button:hover { background: #f1f0ff; }
                .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #4338ca; padding-bottom: 18px; margin-bottom: 24px; position: relative; z-index: 1; }
                .condo-name { font-size: 20px; font-weight: 700; color: #4338ca; }
                .condo-info { font-size: 11px; color: #64748b; line-height: 1.5; margin-top: 6px; }
                .recibo-title { font-size: 18px; font-weight: 700; color: #4338ca; text-align: right; }
                .recibo-num { background: #4338ca; color: #fff; padding: 4px 14px; border-radius: 4px; font-weight: 600; font-size: 0.95rem; margin-top: 6px; display: inline-block; }
                .section-title { font-size: 13px; font-weight: 700; color: #4338ca; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 10px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; }
                .info-grid { display: flex; gap: 16px; margin-bottom: 20px; }
                .info-box { flex: 1; background: #f8fafc; padding: 14px; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 13px; line-height: 1.7; }
                .info-box h6 { color: #4338ca; font-weight: 600; margin-bottom: 8px; font-size: 0.85rem; }
                table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 20px; }
                table th { background: #4338ca; color: white; padding: 10px 12px; text-align: left; font-weight: 600; font-size: 12px; letter-spacing: 0.3px; border: 1px solid #4338ca; }
                .totals-wrap { display: flex; justify-content: flex-end; }
                .totals-box { width: 280px; border: 2px solid #4338ca; border-radius: 8px; overflow: hidden; }
                .total-row { display: flex; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
                .total-final { display: flex; justify-content: space-between; padding: 10px 14px; background: #4338ca; color: #fff; font-weight: 700; font-size: 1.05rem; }
                .pago-method { margin-top: 18px; font-size: 0.85rem; position: relative; z-index: 1; }
                .pago-method strong { color: #4338ca; }
                .disclaimer { margin-top: 16px; padding: 10px 14px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; font-size: 0.75rem; color: #92400e; line-height: 1.4; }
                .firmas { margin-top: 28px; padding-top: 16px; border-top: 1px dashed #cbd5e1; display: flex; justify-content: space-between; }
                .firma { text-align: center; width: 40%; font-size: 0.8rem; color: #64748b; }
                .firma-line { border-top: 1px solid #94a3b8; padding-top: 6px; margin-top: 40px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; font-size: 0.75rem; }
                .footer-text { margin-top: 20px; text-align: center; font-size: 0.7rem; color: #94a3b8; line-height: 1.5; }
                .status-badge { color: #16a34a; font-weight: 600; background: #f0fdf4; padding: 2px 8px; border-radius: 4px; font-size: 0.8rem; }
                @media print {
                    .actions-bar { display: none !important; }
                    body { background: white; padding: 0; }
                    .recibo-container { box-shadow: none; border-radius: 0; padding: 20mm; }
                    .watermark { display: none; }
                }
            </style>
        </head>
        <body>
            <div class="actions-bar">
                <button onclick="window.print()">Imprimir</button>
                <button onclick="window.close()">Cerrar</button>
            </div>
            <div class="recibo-container">
                <div class="watermark">URBANQ</div>

                <!-- Header -->
                <div class="header">
                    <div>
                        <div class="condo-name">&#127970; Condominio UrbanQ</div>
                        <div class="condo-info">
                            <strong>RUC:</strong> XXXXXXXXXXX001<br>
                            <strong>Direccion:</strong> Quito, Ecuador<br>
                            <strong>Telefono:</strong> +593 XXXXXXXXX | <strong>Email:</strong> admin@condominio.com
                        </div>
                    </div>
                    <div style="text-align:right;">
                        <div class="recibo-title">RECIBO DE PAGO</div>
                        <div class="recibo-num">N.&ordm; ${factura.numero}</div>
                        <div style="font-size:11px;color:#64748b;margin-top:6px;">
                            <strong>Fecha:</strong> ${factura.fecha}
                        </div>
                    </div>
                </div>

                <!-- Datos -->
                <div style="position:relative;z-index:1;">
                    <div class="section-title">DATOS DEL RESIDENTE</div>
                    <div class="info-grid">
                        <div class="info-box">
                            <h6>Residente</h6>
                            <strong>${factura.usuario}</strong><br>
                            <strong>Dpto/Casa:</strong> ${factura.apartamento}
                        </div>
                        <div class="info-box">
                            <h6>Datos del Recibo</h6>
                            <strong>Numero:</strong> ${factura.numero}<br>
                            <strong>Estado:</strong> <span class="status-badge">Pagado</span>
                        </div>
                    </div>
                </div>

                <!-- Tabla de conceptos -->
                <div style="position:relative;z-index:1;">
                    <div class="section-title">DETALLE DE CONCEPTOS</div>
                    <table>
                        <thead>
                            <tr>
                                <th>DESCRIPCION</th>
                                <th style="width:80px;text-align:center;">MES</th>
                                <th style="width:120px;text-align:right;">V. UNITARIO</th>
                                <th style="width:120px;text-align:right;">V. TOTAL</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${tableRows}
                        </tbody>
                    </table>
                </div>

                <!-- Totales -->
                <div class="totals-wrap" style="position:relative;z-index:1;">
                    <div class="totals-box">
                        <div class="total-row"><span>SUBTOTAL USD:</span><strong>${monto}</strong></div>
                        <div class="total-row"><span>I.V.A.:</span><span>$0.00</span></div>
                        <div class="total-final"><span>TOTAL USD:</span><span>${monto}</span></div>
                    </div>
                </div>

                <!-- Forma de pago -->
                <div class="pago-method">
                    <strong>FORMA DE PAGO:</strong>
                    <span style="color:#64748b;">&#9744; Efectivo &nbsp; &#9744; Transferencia &nbsp; &#9744; PayPal/Online</span>
                </div>

                <!-- Disclaimer -->
                <div class="disclaimer">
                    Este recibo es un comprobante de pago de expensas y no constituye factura fiscal.
                </div>

                <!-- Firmas -->
                <div class="firmas">
                    <div class="firma"><div class="firma-line">ADMINISTRACION</div></div>
                    <div class="firma"><div class="firma-line">FIRMA CONDOMINO</div></div>
                </div>

                <!-- Footer -->
                <div class="footer-text">
                    Generado electronicamente por UrbanQ &mdash; ${new Date().toLocaleDateString('es-EC')}<br>
                    &copy; ${new Date().getFullYear()} Condominio UrbanQ
                </div>
            </div>
        </body>
        </html>`;

        const ventana = window.open('', '_blank');
        ventana.document.write(reciboHTML);
        ventana.document.close();
    };

    const getIncomesByMonth = () => {
        const incomesByMonth = {};
        const pagados = payments.filter(p => p.estado === 'pagado');

        pagados.forEach(pago => {
            const fecha = new Date(pago.fecha);
            const key = `${fecha.getFullYear()}-${fecha.getMonth()}`;
            const monthName = getMonthName(fecha.getMonth());

            if (!incomesByMonth[key]) {
                incomesByMonth[key] = {
                    mes: monthName,
                    year: fecha.getFullYear(),
                    total: 0,
                    cantidad: 0
                };
            }
            incomesByMonth[key].total += pago.monto;
            incomesByMonth[key].cantidad += 1;
        });

        return Object.values(incomesByMonth).sort((a, b) => {
            if (a.year !== b.year) return b.year - a.year;
            return b.total - a.total;
        });
    };

    // ═══════════════════════════════════════════════════════════════════════
    // FUNCIONES DE EXPORTACION
    // ═══════════════════════════════════════════════════════════════════════

    const exportToExcel = () => {
        const pagados = payments.filter(p => p.estado === 'pagado');

        if (pagados.length === 0) {
            showNotification('No hay datos para exportar', 'error');
            return;
        }

        try {
            // Calcular resumen
            const totalIngresos = pagados.reduce((sum, p) => sum + p.monto, 0);
            const resumen = {
                totalIngresos: totalIngresos,
                totalGastos: 0,
                balance: totalIngresos,
                totalRecibos: pagados.length
            };

            // Construir datos mensuales para hoja de resumen
            const mesesAgrupados = {};
            pagados.forEach(p => {
                const fecha = new Date(p.fecha || p.fechaFormateada);
                const mesKey = fecha.toLocaleDateString('es-EC', { month: 'short' });
                if (!mesesAgrupados[mesKey]) {
                    mesesAgrupados[mesKey] = { mes: mesKey, ingresos: 0, gastos: 0 };
                }
                mesesAgrupados[mesKey].ingresos += p.monto;
            });

            const datosMensuales = Object.values(mesesAgrupados);

            exportarContabilidadExcel(pagados, resumen, datosMensuales, 'UrbanQ');
            showNotification('Reporte Excel exportado exitosamente', 'success');
        } catch (error) {
            console.error('Error exportando Excel:', error);
            showNotification('Error al exportar el reporte', 'error');
        }
    };

    const exportToPDF = () => {
        const pagados = payments.filter(p => p.estado === 'pagado');

        if (pagados.length === 0) {
            showNotification('No hay datos para exportar', 'error');
            return;
        }

        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();

            // Header con logo y titulo
            doc.setFillColor(102, 126, 234);
            doc.rect(0, 0, pageWidth, 35, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.text('UrbanQ', 14, 18);

            doc.setFontSize(12);
            doc.setFont('helvetica', 'normal');
            doc.text('Reporte de Contabilidad', 14, 28);

            // Info del reporte
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            const fechaGeneracion = new Date().toLocaleDateString('es-EC', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
            doc.text(`Fecha de generacion: ${fechaGeneracion}`, 14, 45);

            // Resumen
            const totalIngresos = pagados.reduce((sum, p) => sum + p.monto, 0);
            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total de ingresos: $${totalIngresos.toFixed(2)}`, 14, 53);
            doc.text(`Total de pagos: ${pagados.length}`, 14, 60);

            // Tabla de pagos
            doc.autoTable({
                startY: 70,
                head: [['Fecha', 'Usuario', 'Apt.', 'Concepto', 'Monto']],
                body: pagados.map(p => [
                    p.fechaFormateada,
                    p.usuario,
                    p.apartamento,
                    p.concepto,
                    `$${p.monto.toFixed(2)}`
                ]),
                headStyles: {
                    fillColor: [102, 126, 234],
                    textColor: 255,
                    fontSize: 9,
                    fontStyle: 'bold'
                },
                bodyStyles: {
                    fontSize: 8
                },
                alternateRowStyles: {
                    fillColor: [245, 247, 250]
                },
                columnStyles: {
                    0: { cellWidth: 30 },
                    1: { cellWidth: 40 },
                    2: { cellWidth: 20 },
                    3: { cellWidth: 60 },
                    4: { cellWidth: 25, halign: 'right' }
                },
                margin: { left: 14, right: 14 }
            });

            // Footer
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                const pageHeight = doc.internal.pageSize.getHeight();
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    'Generado por UrbanQ - Sistema de Administracion de Condominios',
                    pageWidth / 2,
                    pageHeight - 10,
                    { align: 'center' }
                );
                doc.text(
                    `Pagina ${i} de ${pageCount}`,
                    pageWidth - 14,
                    pageHeight - 10,
                    { align: 'right' }
                );
            }

            // Guardar PDF
            doc.save(`reporte_contabilidad_${new Date().toISOString().split('T')[0]}.pdf`);
            showNotification('Reporte PDF exportado exitosamente', 'success');
        } catch (error) {
            console.error('Error al generar PDF:', error);
            showNotification('Error al generar PDF. Verifica que jspdf este instalado.', 'error');
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDERIZADO
    // ═══════════════════════════════════════════════════════════════════════

    return (
        <div className="urbanq-contabilidad-container">
            {/* Notificacion */}
            {notification && (
                <div className={`urbanq-contabilidad-notification ${notification.type}`}>
                    {notification.type === 'success' ? <FiCheck /> : <FiAlertCircle />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)}><FiX /></button>
                </div>
            )}

            {/* Tabs */}
            <div className="urbanq-admin-tabs">
                <button
                    className={`urbanq-admin-tab ${activeTab === 'resumen' ? 'active' : ''}`}
                    onClick={() => setActiveTab('resumen')}
                >
                    <FiPieChart /> Resumen
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'facturas' ? 'active' : ''}`}
                    onClick={() => setActiveTab('facturas')}
                >
                    <FiFileText /> Recibos
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'ingresos' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ingresos')}
                >
                    <FiDollarSign /> Ingresos
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'reportes' ? 'active' : ''}`}
                    onClick={() => setActiveTab('reportes')}
                >
                    <FiTrendingUp /> Reportes
                </button>
                <button
                    className={`urbanq-admin-tab ${activeTab === 'configuracion' ? 'active' : ''}`}
                    onClick={() => setActiveTab('configuracion')}
                >
                    <FiSettings /> Configuracion
                </button>

                {/* Tab Finanzas con submenú */}
                <button
                    className={`urbanq-admin-tab financial-tab ${activeTab === 'finanzas' ? 'active' : ''}`}
                    onClick={() => {
                        setActiveTab('finanzas');
                        if (!financialSubTab) {
                            setShowFinancialMenu(true);
                        }
                    }}
                >
                    <FiPieChart /> Finanzas
                    {financialSubTab && activeTab === 'finanzas' && (
                        <span className="financial-tab-indicator"></span>
                    )}
                </button>
            </div>

            {/* Submenú Financiero */}
            <FinancialSubmenu
                isOpen={showFinancialMenu}
                onClose={() => setShowFinancialMenu(false)}
                onSelect={(option) => {
                    setFinancialSubTab(option);
                    setActiveTab('finanzas');
                }}
                currentOption={financialSubTab}
            />

            {/* Contenido */}
            <div className="urbanq-contabilidad-content">
                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: RESUMEN */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'resumen' && (
                    <div className="urbanq-contabilidad-resumen-tab">
                        {/* Metricas principales */}
                        <div className="urbanq-contabilidad-section">
                            <div className="urbanq-contabilidad-section-header">
                                <h2><FiPieChart /> Metricas del Mes</h2>
                                <button
                                    className="urbanq-contabilidad-refresh-btn"
                                    onClick={fetchPayments}
                                    disabled={loading}
                                >
                                    <FiRefreshCw className={loading ? 'spinning' : ''} />
                                </button>
                            </div>

                            <div className="urbanq-contabilidad-metrics-grid">
                                <MetricCard
                                    icon={<FiDollarSign />}
                                    label="Ingresos del Mes"
                                    value={formatCurrency(metrics.ingresosMes)}
                                    color="green"
                                />
                                <MetricCard
                                    icon={<FiCheckCircle />}
                                    label="Pagos Completados"
                                    value={metrics.pagosCompletados}
                                    color="blue"
                                />
                                <MetricCard
                                    icon={<FiFileText />}
                                    label="Recibos Generados"
                                    value={metrics.facturasGeneradas}
                                    color="purple"
                                />
                                <MetricCard
                                    icon={<FiAlertTriangle />}
                                    label="Pendiente por Cobrar"
                                    value={formatCurrency(metrics.pendienteCobrar)}
                                    color="orange"
                                />
                            </div>
                        </div>

                        {/* Estado de Metodos de Pago */}
                        <div className="urbanq-contabilidad-section">
                            <h2><FiCreditCard /> Estado de Metodos de Pago</h2>
                            <div className="urbanq-contabilidad-payment-methods-grid">
                                <PaymentMethodCard
                                    name="PayPal.me"
                                    icon="P"
                                    configured={!!paymentConfig.paypalme}
                                    detail={paymentConfig.paypalme ? `@${paymentConfig.paypalme}` : 'No configurado'}
                                />
                                <PaymentMethodCard
                                    name="Codigo QR"
                                    icon={<FiCreditCard />}
                                    configured={paymentConfig.qrCode === true}
                                    detail={paymentConfig.qrCode ? 'Imagen subida' : 'No configurado'}
                                />
                                <PaymentMethodCard
                                    name="Cuenta Bancaria"
                                    icon={<FiDollarSign />}
                                    configured={paymentConfig.bankAccount === true}
                                    detail={paymentConfig.bankAccount ? 'Datos registrados' : 'No configurado'}
                                />
                            </div>
                            <div className="urbanq-contabilidad-config-hint">
                                <FiInfo />
                                <span>Configura tus metodos de pago en la pestana "Configuracion"</span>
                            </div>
                        </div>

                        {/* Ultimos pagos */}
                        <div className="urbanq-contabilidad-section">
                            <div className="urbanq-contabilidad-section-header">
                                <h2><FiClock /> Ultimos Pagos Recibidos</h2>
                                <button
                                    className="urbanq-contabilidad-btn secondary"
                                    onClick={() => setActiveTab('ingresos')}
                                >
                                    Ver todos <FiChevronRight />
                                </button>
                            </div>

                            <div className="urbanq-contabilidad-recent-payments">
                                {payments.filter(p => p.estado === 'pagado').slice(0, 5).map(pago => (
                                    <div key={pago.id} className="urbanq-contabilidad-payment-item">
                                        <div className="urbanq-contabilidad-payment-icon">
                                            <FiCheck />
                                        </div>
                                        <div className="urbanq-contabilidad-payment-info">
                                            <span className="urbanq-contabilidad-payment-user">{pago.usuario}</span>
                                            <span className="urbanq-contabilidad-payment-concept">{pago.concepto}</span>
                                        </div>
                                        <div className="urbanq-contabilidad-payment-amount">
                                            {formatCurrency(pago.monto)}
                                        </div>
                                        <div className="urbanq-contabilidad-payment-date">
                                            {pago.fechaFormateada}
                                        </div>
                                    </div>
                                ))}
                                {payments.filter(p => p.estado === 'pagado').length === 0 && (
                                    <div className="urbanq-contabilidad-no-data">
                                        <FiInfo />
                                        <p>No hay pagos registrados aun</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: FACTURAS */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'facturas' && (
                    <div className="urbanq-contabilidad-facturas-tab">
                        <div className="urbanq-contabilidad-section">
                            <div className="urbanq-contabilidad-section-header">
                                <h2><FiFileText /> Recibos de Pago ({invoices.length})</h2>
                            </div>

                            {invoices.length > 0 ? (
                                <div className="urbanq-contabilidad-table-container">
                                    <table className="urbanq-contabilidad-table">
                                        <thead>
                                            <tr>
                                                <th>Numero</th>
                                                <th>Cliente</th>
                                                <th>Concepto</th>
                                                <th>Monto</th>
                                                <th>Fecha</th>
                                                <th>Estado</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map(factura => (
                                                <tr key={factura.id}>
                                                    <td className="invoice-number">{factura.numero}</td>
                                                    <td>
                                                        <div className="invoice-client">
                                                            <span className="client-name">{factura.usuario}</span>
                                                            <span className="client-apt">Apt. {factura.apartamento}</span>
                                                        </div>
                                                    </td>
                                                    <td>{factura.concepto}</td>
                                                    <td className="invoice-amount">{formatCurrency(factura.monto)}</td>
                                                    <td>{factura.fecha}</td>
                                                    <td>
                                                        <span className="urbanq-contabilidad-status-badge emitida">
                                                            <FiCheck /> Pagado
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="urbanq-contabilidad-actions">
                                                            <button
                                                                className="urbanq-contabilidad-action-btn"
                                                                onClick={() => viewInvoice(factura)}
                                                                title="Ver recibo"
                                                            >
                                                                <FiEye />
                                                            </button>
                                                            <button
                                                                className="urbanq-contabilidad-action-btn"
                                                                onClick={() => viewInvoice(factura)}
                                                                title="Imprimir"
                                                            >
                                                                <FiPrinter />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="urbanq-contabilidad-empty-state">
                                    <FiFileText className="urbanq-contabilidad-empty-icon" />
                                    <h3>No hay recibos generados</h3>
                                    <p>Los recibos se generan automaticamente cuando se completa un pago</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: INGRESOS */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'ingresos' && (
                    <div className="urbanq-contabilidad-ingresos-tab">
                        <div className="urbanq-contabilidad-section">
                            <div className="urbanq-contabilidad-section-header">
                                <h2><FiDollarSign /> Historial de Ingresos</h2>
                                <div className="urbanq-contabilidad-total-badge">
                                    Total: {formatCurrency(payments.filter(p => p.estado === 'pagado').reduce((s, p) => s + p.monto, 0))}
                                </div>
                            </div>

                            {payments.filter(p => p.estado === 'pagado').length > 0 ? (
                                <div className="urbanq-contabilidad-table-container">
                                    <table className="urbanq-contabilidad-table">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Usuario</th>
                                                <th>Concepto</th>
                                                <th>Monto</th>
                                                <th>Fecha</th>
                                                <th>Apartamento</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {payments.filter(p => p.estado === 'pagado').map(pago => (
                                                <tr key={pago.id}>
                                                    <td className="payment-id">#{pago.id}</td>
                                                    <td>{pago.usuario}</td>
                                                    <td>{pago.concepto}</td>
                                                    <td className="payment-amount positive">{formatCurrency(pago.monto)}</td>
                                                    <td>{pago.fechaFormateada}</td>
                                                    <td>{pago.apartamento}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="urbanq-contabilidad-empty-state">
                                    <FiDollarSign className="urbanq-contabilidad-empty-icon" />
                                    <h3>No hay ingresos registrados</h3>
                                    <p>Los ingresos aparecen aqui cuando se completan pagos</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: REPORTES */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'reportes' && (
                    <div className="urbanq-contabilidad-reportes-tab">
                        <div className="urbanq-contabilidad-section">
                            <h2><FiTrendingUp /> Ingresos por Mes</h2>
                            <p className="urbanq-contabilidad-section-desc">
                                Resumen de ingresos agrupados por mes
                            </p>

                            <div className="urbanq-contabilidad-chart-container">
                                {getIncomesByMonth().length > 0 ? (
                                    <div className="urbanq-contabilidad-bar-chart">
                                        {getIncomesByMonth().map((item, index) => (
                                            <div key={index} className="urbanq-contabilidad-bar-item">
                                                <div className="urbanq-contabilidad-bar-label">
                                                    {item.mes} {item.year}
                                                </div>
                                                <div className="urbanq-contabilidad-bar-wrapper">
                                                    <div
                                                        className="urbanq-contabilidad-bar"
                                                        style={{
                                                            width: `${Math.min((item.total / Math.max(...getIncomesByMonth().map(i => i.total))) * 100, 100)}%`
                                                        }}
                                                    >
                                                        <span className="urbanq-contabilidad-bar-value">
                                                            {formatCurrency(item.total)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="urbanq-contabilidad-bar-count">
                                                    {item.cantidad} pagos
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="urbanq-contabilidad-no-data">
                                        <FiBarChart2 />
                                        <p>No hay datos para mostrar</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="urbanq-contabilidad-section">
                            <h2><FiDownload /> Exportar Reportes</h2>
                            <div className="urbanq-contabilidad-export-buttons">
                                <button
                                    className="urbanq-contabilidad-btn primary"
                                    onClick={exportToExcel}
                                >
                                    <FiDownload /> Exportar a Excel
                                </button>
                                <button
                                    className="urbanq-contabilidad-btn secondary"
                                    onClick={exportToPDF}
                                >
                                    <FiDownload /> Exportar a PDF
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: CONFIGURACION */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'configuracion' && (
                    <div className="urbanq-contabilidad-config-tab">
                        <div className="urbanq-contabilidad-section">
                            <h2><FiSettings /> Configuracion de Metodos de Pago</h2>
                            <p className="urbanq-contabilidad-section-desc">
                                Configura los metodos de pago disponibles para los residentes
                            </p>

                            <div className="urbanq-contabilidad-config-grid">
                                <div className={`urbanq-contabilidad-config-card ${paymentConfig.paypalme ? 'configured' : ''}`}>
                                    <div className="urbanq-contabilidad-config-icon paypal">P</div>
                                    <div className="urbanq-contabilidad-config-info">
                                        <h4>PayPal.me</h4>
                                        <p>{paymentConfig.paypalme ? `@${paymentConfig.paypalme}` : 'No configurado'}</p>
                                    </div>
                                    <div className="urbanq-contabilidad-config-status">
                                        {paymentConfig.paypalme ? (
                                            <span className="status-ok"><FiCheck /> Activo</span>
                                        ) : (
                                            <span className="status-pending"><FiAlertCircle /> Pendiente</span>
                                        )}
                                    </div>
                                </div>

                                <div className={`urbanq-contabilidad-config-card ${paymentConfig.qrCode === true ? 'configured' : ''}`}>
                                    <div className="urbanq-contabilidad-config-icon qr">
                                        <FiCreditCard />
                                    </div>
                                    <div className="urbanq-contabilidad-config-info">
                                        <h4>Codigo QR</h4>
                                        <p>{paymentConfig.qrCode === true ? 'Imagen cargada' : 'No configurado'}</p>
                                    </div>
                                    <div className="urbanq-contabilidad-config-status">
                                        {paymentConfig.qrCode === true ? (
                                            <span className="status-ok"><FiCheck /> Activo</span>
                                        ) : (
                                            <span className="status-pending"><FiAlertCircle /> Pendiente</span>
                                        )}
                                    </div>
                                </div>

                                <div className={`urbanq-contabilidad-config-card ${paymentConfig.bankAccount === true ? 'configured' : ''}`}>
                                    <div className="urbanq-contabilidad-config-icon bank">
                                        <FiDollarSign />
                                    </div>
                                    <div className="urbanq-contabilidad-config-info">
                                        <h4>Cuenta Bancaria</h4>
                                        <p>{paymentConfig.bankAccount === true ? 'Datos registrados' : 'No configurado'}</p>
                                    </div>
                                    <div className="urbanq-contabilidad-config-status">
                                        {paymentConfig.bankAccount === true ? (
                                            <span className="status-ok"><FiCheck /> Activo</span>
                                        ) : (
                                            <span className="status-pending"><FiAlertCircle /> Pendiente</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="urbanq-contabilidad-config-action">
                                <button
                                    className="urbanq-contabilidad-btn primary large"
                                    onClick={() => setShowPaymentConfigModal(true)}
                                >
                                    <FiSettings /> Abrir Configuracion de Pagos
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════ */}
                {/* TAB: FINANZAS (con submenú) */}
                {/* ═══════════════════════════════════════════════════════════════ */}
                {activeTab === 'finanzas' && (
                    <div className="urbanq-finanzas-wrapper">
                        {/* Breadcrumb */}
                        {financialSubTab && (
                            <div className="financial-breadcrumb">
                                <FiPieChart />
                                <span>Finanzas</span>
                                <span className="financial-breadcrumb-separator">›</span>
                                <span className="financial-breadcrumb-current">
                                    {menuOptions.find(o => o.id === financialSubTab)?.title || ''}
                                </span>
                                <button
                                    className="financial-breadcrumb-back"
                                    onClick={() => setShowFinancialMenu(true)}
                                >
                                    Cambiar sección
                                </button>
                            </div>
                        )}

                        {/* Sin subopción seleccionada */}
                        {!financialSubTab && (
                            <div className="urbanq-finanzas-empty-state">
                                <FiPieChart style={{ fontSize: 48, color: '#667eea', marginBottom: 16 }} />
                                <h3>Sistema Financiero</h3>
                                <p>Selecciona una opción del menú para comenzar</p>
                                <button
                                    className="urbanq-contabilidad-btn primary"
                                    onClick={() => setShowFinancialMenu(true)}
                                >
                                    Abrir menú de opciones
                                </button>
                            </div>
                        )}

                        {/* Selector de fecha (para tabs que lo necesitan) */}
                        {financialSubTab && financialSubTab !== 'config_financiera' && (
                            <div className="urbanq-finanzas-year-selector">
                                <YearMonthSelector
                                    year={finYear}
                                    month={finMonth}
                                    onYearChange={setFinYear}
                                    onMonthChange={setFinMonth}
                                    showMonth={financialSubTab !== 'resumen_anual'}
                                    variant="gradient"
                                    minYear={2020}
                                    maxYear={2030}
                                />
                            </div>
                        )}

                        {/* Renderizar componente según subopción */}
                        {financialSubTab === 'panel_financiero' && (
                            <FinancialDashboardTab
                                year={finYear}
                                month={finMonth}
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                            />
                        )}

                        {financialSubTab === 'mis_ingresos' && (
                            <IncomeTab
                                year={finYear}
                                month={finMonth}
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                            />
                        )}

                        {financialSubTab === 'mis_gastos' && (
                            <ExpenseTab
                                year={finYear}
                                month={finMonth}
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                            />
                        )}

                        {financialSubTab === 'presupuesto' && (
                            <BudgetTab
                                year={finYear}
                                month={finMonth}
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                            />
                        )}

                        {financialSubTab === 'resumen_anual' && (
                            <AnnualSummaryTab
                                year={finYear}
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                            />
                        )}

                        {financialSubTab === 'calendario' && (
                            <FinancialCalendar
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                                userId={user?.id}
                            />
                        )}

                        {financialSubTab === 'config_financiera' && (
                            <FinancialConfigTab
                                token={token}
                                apiUrl={API_URL}
                                scope="condominium"
                                onNotification={showNotification}
                            />
                        )}
                    </div>
                )}
            </div>

            {/* Modal de Configuracion de Pagos */}
            {showPaymentConfigModal && (
                <PaymentConfigModal
                    show={showPaymentConfigModal}
                    onHide={() => {
                        setShowPaymentConfigModal(false);
                        fetchPaymentConfig();
                    }}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES
// ═══════════════════════════════════════════════════════════════════════════

function MetricCard({ icon, label, value, color }) {
    return (
        <div className={`urbanq-contabilidad-metric-card ${color}`}>
            <div className="urbanq-contabilidad-metric-icon">
                {icon}
            </div>
            <div className="urbanq-contabilidad-metric-content">
                <span className="urbanq-contabilidad-metric-value">{value}</span>
                <span className="urbanq-contabilidad-metric-label">{label}</span>
            </div>
        </div>
    );
}

function PaymentMethodCard({ name, icon, configured, detail }) {
    return (
        <div className={`urbanq-contabilidad-payment-method-card ${configured ? 'configured' : 'not-configured'}`}>
            <div className="urbanq-contabilidad-payment-method-icon">
                {typeof icon === 'string' ? icon : icon}
            </div>
            <div className="urbanq-contabilidad-payment-method-info">
                <span className="urbanq-contabilidad-payment-method-name">{name}</span>
                <span className="urbanq-contabilidad-payment-method-detail">{detail}</span>
            </div>
            <div className={`urbanq-contabilidad-payment-method-status ${configured ? 'active' : 'inactive'}`}>
                {configured ? <FiCheck /> : <FiX />}
                <span>{configured ? 'Configurado' : 'No configurado'}</span>
            </div>
        </div>
    );
}
